import { cssColour, paletteTable } from './palettes';

/*
 * The thermal legend: a vertical scale in degrees Celsius that follows the
 * palette's range (docs/FIELD_AI_VISION_SPEC.md section 42). Pure: the panel
 * draws what these give.
 *
 *   niceStep(span, maxTicks)      1, 2, 2.5 or 5 times a power of ten
 *   legendTicks(lo, hi, maxTicks) {step, decimals, ticks: [{value, at}]}
 *                                 `at` is 0 at lo and 1 at hi, so a vertical
 *                                 scale draws a tick at (1 - at) of its height
 *   formatCelsius(value, decimals)
 *   legendGradient(palette, stops) a CSS linear-gradient, coldest at the bottom
 */
const MANTISSAS = [1, 2, 2.5, 5, 10];

/** A round step that puts about `maxTicks` ticks across `span` (one more when both ends fall on it). */
export function niceStep(span, maxTicks = 6) {
  if (!(span > 0) || !Number.isFinite(span)) return 1;
  const rough = span / Math.max(1, maxTicks);
  const power = 10 ** Math.floor(Math.log10(rough));
  const mantissa = MANTISSAS.find(m => m * power >= rough - 1e-12) || 10;
  return mantissa * power;
}

/** The decimals a step needs: 0 for 1 or 5, 1 for 0.5, 2 for 0.25 ... */
export function decimalsFor(step) {
  if (!(step > 0)) return 0;
  for (let d = 0; d <= 6; d += 1) {
    const scaled = step * 10 ** d;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return d;
  }
  return 6;
}

/** Round-number ticks between lo and hi (both included when they fall on the step). */
export function legendTicks(lo, hi, maxTicks = 6) {
  if (lo === null || hi === null || lo === undefined || hi === undefined
      || !Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { step: null, decimals: 0, ticks: [] };
  }
  if (!(hi > lo)) return { step: 0, decimals: 1, ticks: [{ value: lo, at: 0.5 }] };
  const step = niceStep(hi - lo, maxTicks);
  const decimals = decimalsFor(step);
  const ticks = [];
  const first = Math.ceil(lo / step - 1e-9);
  const last = Math.floor(hi / step + 1e-9);
  for (let k = first; k <= last; k += 1) {
    const value = Number((k * step).toFixed(decimals));
    ticks.push({ value, at: (value - lo) / (hi - lo) });
  }
  return { step, decimals, ticks };
}

/** "23.4 °C", with a minus sign for below zero; an em dash for no value. */
export function formatCelsius(value, decimals = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = Math.abs(value).toFixed(decimals);
  const negative = value < 0 && Number(text) !== 0;
  return `${negative ? '−' : ''}${text} °C`;
}

/** A signed difference: "+1.2 °C", "−0.4 °C", "0.0 °C". */
export function formatDelta(value, decimals = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = Math.abs(value).toFixed(decimals);
  if (Number(text) === 0) return `${text} °C`;
  return `${value > 0 ? '+' : '−'}${text} °C`;
}

/** The palette as a CSS gradient, coldest at the bottom (`to top`). */
export function legendGradient(palette, stops = 16) {
  const table = paletteTable(palette);
  const parts = [];
  for (let i = 0; i < stops; i += 1) {
    const index = Math.round((i / (stops - 1)) * 255);
    parts.push(`${cssColour(table[index])} ${((i / (stops - 1)) * 100).toFixed(1)}%`);
  }
  return `linear-gradient(to top, ${parts.join(', ')})`;
}
